/**
 * Card — PWA port of client-native/src/ui/atoms/visual/Card.tsx (source of truth).
 *
 * Rounded white surface (radius --radius-card / 32px, default padding 16) with
 * four shadow variants matching iOS:
 *   plain          → base surface shadow (--shadow-card), no border
 *   border (default) → --shadow-card-border
 *   borderElevated → --shadow-card-elevated
 *   floating       → --shadow-card-floating
 *
 * Tokens are referenced via CSS vars (variables.css); no hardcoded hex/px that a
 * token already covers.
 */
import type { CSSProperties, ReactNode } from "react";

export type CardVariant = "plain" | "border" | "borderElevated" | "floating";

export interface CardProps {
  children: ReactNode;
  variant?: CardVariant;
  /** Inner padding in px (matches iOS `padding`, default 16). */
  padding?: number;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

const VARIANT_SHADOW: Record<CardVariant, string> = {
  plain: "var(--shadow-card)",
  border: "var(--shadow-card-border)",
  borderElevated: "var(--shadow-card-elevated)",
  floating: "var(--shadow-card-floating)",
};

const Card: React.FC<CardProps> = ({
  children,
  variant = "border",
  padding = 16,
  className,
  style,
  onClick,
}) => {
  const finalStyle: CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: "var(--radius-card)",
    padding,
    boxShadow: VARIANT_SHADOW[variant],
    boxSizing: "border-box",
    ...style,
  };

  return (
    <div className={className} style={finalStyle} onClick={onClick}>
      {children}
    </div>
  );
};

export default Card;
